import { useCallback, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';

import { useMutationCreateSetupIntent } from './useMutationCreateSetupIntent';

import { PAYMENT_METHOD } from '~common';
import { useLanguageContext } from '~providers';

type ConfirmedWalletPaymentMethod = {
  paymentMethodId: string;
  method: PAYMENT_METHOD;
};

const UNKNOWN_ERROR_KEY = 'text.hooks.useWalletCheckout.error';
const UNKNOWN_ERROR_FALLBACK =
  'An unknown error occurred while preparing the wallet payment';

/**
 * Wallet checkout via `ExpressCheckoutElement` + `stripe.confirmSetup` (ENG-7890).
 *
 * Replaces the deprecated `stripe.paymentRequest()` flow: on the ECE `confirm`
 * event it validates the collected data, creates a backend SetupIntent
 * (ENG-7871), confirms it with the wallet, and returns the resulting Stripe
 * payment-method id so the caller can start the rental via `useRentCharger`.
 *
 * It deliberately does NOT own `useRentCharger` (the caller does) so wallet,
 * saved-card and campus-card paths share one renting/dialog state.
 */
export const useWalletCheckout = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { lt } = useLanguageContext();
  const createSetupIntentMutation = useMutationCreateSetupIntent();

  const [walletError, setWalletError] = useState<string | null>(null);
  const [isConfirmingWallet, setIsConfirmingWallet] = useState(false);

  const confirmWalletPaymentMethod = useCallback(
    async (
      event: StripeExpressCheckoutElementConfirmEvent,
    ): Promise<ConfirmedWalletPaymentMethod | null> => {
      if (!stripe || !elements) return null;

      setWalletError(null);
      setIsConfirmingWallet(true);

      try {
        // 1. Validate the data collected by the Express Checkout Element.
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setWalletError(
            submitError.message ??
              lt(UNKNOWN_ERROR_KEY, UNKNOWN_ERROR_FALLBACK),
          );
          return null;
        }

        // 2. Create the SetupIntent on the backend. Wallets authenticate
        //    in-app, so keep redirects off to stay inline.
        const { clientSecret } = await createSetupIntentMutation.mutateAsync(
          {},
        );

        // 3. Confirm the SetupIntent with the wallet payment method.
        const { setupIntent, error } = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });

        if (error) {
          setWalletError(
            lt(
              `stripeError.${error.code}`,
              error.message ?? lt(UNKNOWN_ERROR_KEY, UNKNOWN_ERROR_FALLBACK),
            ),
          );
          return null;
        }

        const paymentMethodId =
          typeof setupIntent?.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent?.payment_method?.id;

        if (!paymentMethodId) {
          setWalletError(lt(UNKNOWN_ERROR_KEY, UNKNOWN_ERROR_FALLBACK));
          return null;
        }

        const method =
          event.expressPaymentType === 'google_pay'
            ? PAYMENT_METHOD.GOOGLE_PAY
            : PAYMENT_METHOD.APPLE_PAY;

        return { paymentMethodId, method };
      } catch (err) {
        setWalletError(lt(UNKNOWN_ERROR_KEY, UNKNOWN_ERROR_FALLBACK));
        return null;
      } finally {
        setIsConfirmingWallet(false);
      }
    },
    [stripe, elements, createSetupIntentMutation, lt],
  );

  return {
    confirmWalletPaymentMethod,
    walletError,
    isConfirmingWallet,
  };
};
